// PDF of one Consumer Voice audit (product or brand) — light layout, one
// flowing document. Follows the react-pdf rules from the PDP audit PDF:
// stacked Texts never sit beside a flex:1 sibling inside a centered row,
// opaque hex colors only, fonts registered per weight (registerPdfFonts).
import React from 'react';
import { Document, Link, Page, Polyline, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { registerPdfFonts } from '../../utils';
import {
  ConsumerVoiceAudit,
  ConsumerVoiceBrandReport,
  ConsumerVoiceProductReport,
  CvContentOpportunity,
  CvDemand,
  CvDemandTrends,
  CvQuote,
  CV_OBJECTIVE_SCORES,
  CV_OPPORTUNITY_TYPE_LABELS,
  cvDiscoveredLine,
  cvSourceLabel,
} from '../../types/consumerVoice';

registerPdfFonts();

const GOLD = '#b8966a';
const INK = '#1C1A17';
const MUTED = '#7A7268';
const FAINT = '#9B958C';
const BORDER = '#E5E1DB';
const GREEN = '#2E7D32';
const RED = '#c62828';

const s = StyleSheet.create({
  page: { padding: 40, fontFamily: 'Lato', fontSize: 10, color: INK, lineHeight: 1.45 },
  kicker: { fontFamily: 'Oswald-Medium', fontSize: 9, color: GOLD, letterSpacing: 1, textTransform: 'uppercase' },
  title: { fontFamily: 'Oswald-Bold', fontSize: 22, marginTop: 4 },
  meta: { fontSize: 9, color: MUTED, marginTop: 4 },
  strip: { flexDirection: 'row', marginTop: 16 },
  stripSmall: { flexDirection: 'row', marginTop: 8 },
  card: { flex: 1, borderWidth: 1, borderColor: BORDER, borderRadius: 6, padding: 10, marginRight: 8 },
  cardLast: { marginRight: 0 },
  cardLabel: { fontFamily: 'Lato-Bold', fontSize: 8, color: FAINT, textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontFamily: 'Oswald-Bold', fontSize: 24, marginTop: 4 },
  cardValueSmall: { fontFamily: 'Oswald-Bold', fontSize: 18, marginTop: 2 },
  cardCaption: { fontSize: 8, color: MUTED, marginTop: 4 },
  h2: { fontFamily: 'Oswald-Medium', fontSize: 13, marginTop: 18, marginBottom: 6, color: INK },
  h3: { fontFamily: 'Lato-Bold', fontSize: 9, color: FAINT, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 8, marginBottom: 3 },
  body: { fontSize: 10, marginTop: 8 },
  item: { marginBottom: 6 },
  itemTitle: { fontFamily: 'Lato-Bold', fontSize: 10 },
  itemSub: { fontSize: 9, color: MUTED, marginTop: 1 },
  quote: { fontSize: 9, color: MUTED, fontStyle: 'italic', marginTop: 2, paddingLeft: 8, borderLeftWidth: 2, borderLeftColor: BORDER },
  link: { color: GOLD, textDecoration: 'none' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 },
  chip: { fontSize: 8, color: MUTED, borderWidth: 1, borderColor: BORDER, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2, marginRight: 4, marginBottom: 4 },
  chipUp: { color: GREEN, borderColor: GREEN },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: BORDER, paddingVertical: 4 },
  rowHead: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: INK, paddingVertical: 4 },
  cellName: { flex: 3, fontSize: 9 },
  cellScore: { flex: 1, fontSize: 9, fontFamily: 'Lato-Bold' },
  cellCov: { flex: 1, fontSize: 9, color: MUTED },
  cellRead: { flex: 4, fontSize: 9, color: MUTED },
  oppRow: { flexDirection: 'row', marginBottom: 6 },
  oppScore: { width: 34, fontFamily: 'Oswald-Bold', fontSize: 14 },
  oppBody: { flex: 1 },
  footer: { position: 'absolute', bottom: 22, left: 40, right: 40, fontSize: 8, color: FAINT, flexDirection: 'row', justifyContent: 'space-between' },
});

const tone = (v: number | null | undefined) => {
  if (v == null) return MUTED;
  if (v >= 70) return GREEN;
  if (v >= 45) return GOLD;
  return RED;
};
const sentimentColor = (sentiment: string) => {
  if (sentiment === 'positive') return GREEN;
  if (sentiment === 'negative') return RED;
  return GOLD;
};
const risingSuffix = (q: { breakout?: boolean; value: number | null }) => {
  if (q.breakout) return ' · breakout';
  if (q.value != null) return ` · +${q.value}%`;
  return '';
};
const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const TREND_LABEL: Record<string, string> = { breakout: 'Breakout', rising: 'Rising', stable: 'Stable', falling: 'Falling', none: 'No search interest' };

function Quotes({ quotes }: { quotes?: CvQuote[] }) {
  if (!quotes || !quotes.length) return null;
  return (
    <View>
      {quotes.slice(0, 2).map((q, i) => (
        <Text key={i} style={s.quote}>
          “{q.text}” {q.url ? <Link src={q.url} style={s.link}>source</Link> : null}
        </Text>
      ))}
    </View>
  );
}

// 12-month interest lines as inline SVG polylines (react-pdf has no
// chart primitive). Draws every compared term on the explore's shared
// 0-100 scale like the web chart, so a product term that scores 0 all
// year sits under the brand and category lines instead of drawing a lone
// flat line. Older analyses only carry the primary term's series.
const SERIES_COLORS = [GOLD, '#5B8DEF', '#9C6ADE', '#E0A030', '#4CAF7D'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
type TrendLine = { term: string; points: Array<{ date: string; value: number }> };
const trendLines = (tr: CvDemandTrends): TrendLine[] => {
  const byTerm = tr.series_by_term && tr.series_by_term.length
    ? tr.series_by_term
    : [{ term: tr.primary || 'Search interest', points: tr.interest_over_time || [] }];
  return byTerm.map((l) => ({ term: l.term, points: l.points || [] })).filter((l) => l.points.length > 1);
};

function TrendChart({ lines }: { lines: TrendLine[] }) {
  const w = 515;
  const h = 80;
  const n = Math.max(...lines.map((l) => l.points.length));
  const step = w / (n - 1);
  const y = (v: number) => (h - (Math.max(0, Math.min(100, v)) / 100) * h).toFixed(1);
  // One label where a quarter starts (Jan/Apr/Jul/Oct).
  const ticks: Array<{ x: number; label: string }> = [];
  let prevMonth = -1;
  lines[0].points.forEach((pt, i) => {
    const d = new Date(pt.date);
    const m = d.getUTCMonth();
    if (!Number.isNaN(m) && m !== prevMonth && m % 3 === 0) ticks.push({ x: i * step, label: `${MONTHS[m]} ${String(d.getUTCFullYear()).slice(2)}` });
    if (!Number.isNaN(m)) prevMonth = m;
  });
  return (
    <View style={{ marginTop: 6 }}>
      <Svg width={w} height={h + 2} viewBox={`0 0 ${w} ${h + 2}`}>
        <Polyline points={`0,${y(50)} ${w},${y(50)}`} stroke={BORDER} strokeWidth={0.5} fill="none" />
        <Polyline points={`0,${h} ${w},${h}`} stroke={BORDER} strokeWidth={1} fill="none" />
        {lines.map((l, li) => (
          <Polyline
            key={l.term}
            points={l.points.map((p, i) => `${(i * step).toFixed(1)},${y(p.value)}`).join(' ')}
            stroke={SERIES_COLORS[li % SERIES_COLORS.length]}
            strokeWidth={1.5}
            fill="none"
          />
        ))}
      </Svg>
      <View style={{ height: 12, position: 'relative' }}>
        {ticks.map((t) => (
          <Text key={t.label} style={{ position: 'absolute', left: Math.min(t.x, w - 36), top: 0, fontSize: 7, color: FAINT }}>{t.label}</Text>
        ))}
      </View>
      {lines.length > 1 ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 2 }}>
          {lines.map((l, li) => (
            <View key={l.term} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12, marginBottom: 2 }}>
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: SERIES_COLORS[li % SERIES_COLORS.length], marginRight: 4 }} />
              <Text style={{ fontSize: 8, color: MUTED }}>{l.term}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function DemandBlock({ demand }: { demand: CvDemand }) {
  const tr = demand.trends;
  const kp = demand.keyword_planner;
  const sc = demand.search_console;
  const lines = tr ? trendLines(tr) : [];
  const peak = lines.reduce((m, l) => Math.max(m, ...l.points.map((p) => p.value || 0)), 0);
  if (!tr && !kp && !sc && !demand.youtube && !demand.retailer) {
    return (
      <>
        <Text style={s.h2}>Search demand</Text>
        <Text style={s.itemSub}>No search-demand sources ran for this analysis.</Text>
      </>
    );
  }
  return (
    <>
      <Text style={s.h2}>Search demand</Text>
      {demand.keywords && demand.keywords.length ? <Text style={s.itemSub}>Terms analyzed: {demand.keywords.join(' · ')}</Text> : null}
      {tr ? (
        <View>
          <View wrap={false}>
            <Text style={s.h3}>Google Trends · {tr.primary || demand.keywords[0]}{tr.partial ? ' · partial data' : ''}</Text>
            <Text>
              {tr.trend_direction ? TREND_LABEL[tr.trend_direction] || tr.trend_direction : ''}
              {tr.query_growth_pct != null ? ` · ${tr.query_growth_pct >= 0 ? '+' : ''}${tr.query_growth_pct}% last 90 days vs prior` : ''}
              {tr.avg_12m != null ? ` · 12-month average interest ${tr.avg_12m}` : ''}
            </Text>
            {tr.headline_term && tr.primary && tr.headline_term !== tr.primary ? (
              <Text style={s.itemSub}>“{tr.primary}” has no measurable search interest on its own — momentum is read from “{tr.headline_term}”.</Text>
            ) : null}
            {lines.length && peak > 0 ? <TrendChart lines={lines} /> : null}
            {lines.length && peak <= 0 ? <Text style={s.itemSub}>No measurable Google search interest for any compared term in the last 12 months.</Text> : null}
            {tr.comparison_share && Object.keys(tr.comparison_share).length > 1 ? (
              <Text style={s.itemSub}>
                Share of interest: {Object.keys(tr.comparison_share).map((k) => `${k} ${pct(tr.comparison_share ? tr.comparison_share[k] : null)}`).join(' · ')}
              </Text>
            ) : null}
          </View>
          {tr.related_queries && tr.related_queries.rising.length ? (
            <View wrap={false}>
              <Text style={s.h3}>Rising searches</Text>
              <View style={s.chipRow}>
                {tr.related_queries.rising.slice(0, 12).map((q, i) => (
                  <Text key={i} style={q.breakout ? [s.chip, s.chipUp] : s.chip}>
                    {q.query}
                    {risingSuffix(q)}
                  </Text>
                ))}
              </View>
            </View>
          ) : null}
          {tr.related_queries && tr.related_queries.top.length ? (
            <View wrap={false}>
              <Text style={s.h3}>Top related searches</Text>
              <View style={s.chipRow}>
                {tr.related_queries.top.slice(0, 12).map((q, i) => (
                  <Text key={i} style={s.chip}>{q.query}</Text>
                ))}
              </View>
            </View>
          ) : null}
          {tr.geo_interest && tr.geo_interest.length ? <Text style={s.itemSub}>Strongest regions: {tr.geo_interest.slice(0, 5).map((g) => g.region).join(', ')}</Text> : null}
        </View>
      ) : null}
      {kp ? (
        <View wrap={false}>
          <Text style={s.h3}>Google Keyword Planner · average monthly US searches</Text>
          <Text>
            Product searches {fmt(kp.search_volume)}/mo · brand {fmt(kp.search_volume_brand)} · category {fmt(kp.search_volume_category)}
            {kp.query_growth_pct != null ? ` · last 3 months vs prior ${kp.query_growth_pct >= 0 ? '+' : ''}${kp.query_growth_pct}%` : ''}
          </Text>
          {kp.related_keywords && kp.related_keywords.length ? (
            <View style={s.chipRow}>
              {kp.related_keywords.slice(0, 15).map((k, i) => (
                <Text key={i} style={s.chip}>{k.text} · {fmt(k.avg_monthly_searches)}</Text>
              ))}
            </View>
          ) : null}
        </View>
      ) : null}
      {sc ? (
        <View wrap={false}>
          <Text style={s.h3}>Google Search Console · {sc.domain}{sc.window ? ` · ${sc.window.startDate} to ${sc.window.endDate}` : ''}</Text>
          <Text>
            {fmt(sc.impressions)} impressions · {fmt(sc.clicks)} clicks · CTR {pct(sc.ctr)} · avg position {sc.position == null ? '—' : sc.position}
          </Text>
          {sc.top_queries && sc.top_queries.length ? (
            <View style={s.chipRow}>
              {sc.top_queries.slice(0, 15).map((q, i) => (
                <Text key={i} style={s.chip}>{q.query} · {fmt(q.impressions)}</Text>
              ))}
            </View>
          ) : null}
        </View>
      ) : null}
      {demand.youtube || demand.retailer ? (
        <Text style={s.itemSub}>
          {demand.youtube ? `YouTube: ${demand.youtube.videos} review videos, ${fmt(demand.youtube.total_views)} views, ${demand.youtube.comments} comments read. ` : ''}
          {demand.retailer
            ? `Retailer pages: ${fmt(demand.retailer.review_count)} reviews${demand.retailer.avg_rating != null ? ` averaging ${demand.retailer.avg_rating}/5` : ''}${demand.retailer.question_count != null ? `, ${demand.retailer.question_count} shopper questions (${demand.retailer.unanswered_question_count ?? 0} unanswered)` : ''}.`
            : ''}
        </Text>
      ) : null}
    </>
  );
}

function Opportunities({ rows, title, showProducts }: { rows: CvContentOpportunity[]; title: string; showProducts?: boolean }) {
  return (
    <>
      <Text style={s.h2}>{title}</Text>
      {rows.length ? (
        rows.map((o) => (
          <View key={o.key} style={s.oppRow} wrap={false}>
            <Text style={[s.oppScore, { color: tone(o.score) }]}>{String(o.score)}</Text>
            <View style={s.oppBody}>
              <Text style={s.itemTitle}>
                {o.title} <Text style={{ color: MUTED, fontFamily: 'Lato' }}>({CV_OPPORTUNITY_TYPE_LABELS[o.type] || o.type}{o.sources && o.sources.length ? `; ${o.sources.map((x) => x.replace(/_/g, ' ')).join(', ')}` : ''})</Text>
              </Text>
              {showProducts && o.products && o.products.length ? <Text style={s.itemSub}>{o.products.join(', ')}</Text> : null}
              {(o.evidence || []).slice(0, 2).map((e, i) => (
                <Text key={i} style={s.quote}>
                  {e.text} {e.url ? <Link src={e.url} style={s.link}>source</Link> : null}
                </Text>
              ))}
            </View>
          </View>
        ))
      ) : (
        <Text style={s.itemSub}>Nothing shoppers ask or search for went unanswered in the data.</Text>
      )}
    </>
  );
}

function ProductPages({ audit, report }: { audit: ConsumerVoiceAudit; report: ConsumerVoiceProductReport }) {
  return (
    <>
      <Text style={s.body}>{report.summary}</Text>
      <Text style={s.meta}>
        Coverage {report.coverage} · {report.relevant_mention_count} relevant mentions
        {audit.subscores && audit.subscores.confidence != null ? ` · confidence ${Math.round(audit.subscores.confidence * 100)}%` : ''}
      </Text>

      {report.demand ? <DemandBlock demand={report.demand} /> : null}
      {report.content_opportunities ? <Opportunities rows={report.content_opportunities} title="Content opportunities" /> : null}

      <Text style={s.h2}>Themes</Text>
      {report.themes.length ? (
        report.themes.map((t, i) => (
          <View key={i} style={s.item} wrap={false}>
            <Text style={s.itemTitle}>
              {t.theme} <Text style={{ color: sentimentColor(t.sentiment) }}>({t.sentiment}, {t.frequency})</Text>
            </Text>
            <Text style={s.itemSub}>{t.insight}</Text>
            <Quotes quotes={t.quotes} />
          </View>
        ))
      ) : (
        <Text style={s.itemSub}>No themes — too little relevant discussion.</Text>
      )}

      <Text style={s.h2}>Praise</Text>
      {report.praise.length ? report.praise.map((p, i) => (
        <View key={i} style={s.item} wrap={false}>
          <Text>{p.text}</Text>
          <Quotes quotes={p.quotes} />
        </View>
      )) : <Text style={s.itemSub}>None found.</Text>}

      <Text style={s.h2}>Pain points</Text>
      {report.pain_points.length ? report.pain_points.map((p, i) => (
        <View key={i} style={s.item} wrap={false}>
          <Text>{p.text} <Text style={{ color: MUTED }}>[{p.severity}]</Text></Text>
          <Quotes quotes={p.quotes} />
        </View>
      )) : <Text style={s.itemSub}>None found.</Text>}

      <Text style={s.h2}>Purchase objections</Text>
      {report.objections.length ? report.objections.map((o, i) => (
        <View key={i} style={s.item} wrap={false}>
          <Text>{o.text}{o.category ? <Text style={{ color: MUTED }}> [{o.category.replace(/_/g, ' ')}]</Text> : null}</Text>
          <Quotes quotes={o.quotes} />
        </View>
      )) : <Text style={s.itemSub}>None found.</Text>}

      <Text style={s.h2}>Unanswered shopper questions</Text>
      {report.unanswered_questions.length ? report.unanswered_questions.map((q, i) => (
        <Text key={i} style={s.item}>• {q.question}{q.topic ? <Text style={{ color: MUTED }}> [{q.topic.replace(/_/g, ' ')}]</Text> : null} {q.url ? <Link src={q.url} style={s.link}>source</Link> : null}</Text>
      )) : <Text style={s.itemSub}>None captured.</Text>}

      {report.comparisons.length ? (
        <>
          <Text style={s.h2}>Compared against</Text>
          {report.comparisons.map((c, i) => (
            <Text key={i} style={s.item}>• <Text style={s.itemTitle}>{c.competitor}</Text> — {c.context}</Text>
          ))}
        </>
      ) : null}

      {report.personas.length || report.use_cases.length ? (
        <>
          <Text style={s.h2}>Personas and use cases</Text>
          <View style={s.chipRow}>
            {[...report.personas, ...report.use_cases].map((p, i) => (
              <Text key={i} style={s.chip}>{p}</Text>
            ))}
          </View>
        </>
      ) : null}

      {report.copy_bank.length ? (
        <>
          <Text style={s.h2}>Customer language copy bank</Text>
          {report.copy_bank.map((c, i) => (
            <Text key={i} style={s.quote}>“{c.phrase}”</Text>
          ))}
        </>
      ) : null}

      <Text style={s.h2}>Sources</Text>
      {cvDiscoveredLine(report.meta) ? <Text style={s.itemSub}>{cvDiscoveredLine(report.meta)}</Text> : null}
      {report.sources.length ? report.sources.map((src, i) => (
        <Text key={i} style={{ fontSize: 8.5, color: MUTED, marginBottom: 2 }}>
          [{i + 1}] {cvSourceLabel(src) ? `${cvSourceLabel(src)} — ` : ''}<Link src={src.url} style={s.link}>{src.title || src.url}</Link>
        </Text>
      )) : <Text style={s.itemSub}>No sources.</Text>}
    </>
  );
}

function BrandPages({ report }: { report: ConsumerVoiceBrandReport }) {
  const hasIndices = report.per_product.some((p) => p.demand_index != null || p.opportunity_index != null);
  return (
    <>
      <Text style={s.body}>{report.summary}</Text>
      <Text style={s.meta}>{report.products_with_data} of {report.products_analyzed} analyzed products had usable consumer discussion.</Text>

      <Text style={s.h2}>Products</Text>
      <View style={s.rowHead}>
        <Text style={s.cellName}>Product</Text>
        <Text style={s.cellScore}>Score</Text>
        {hasIndices ? <Text style={s.cellScore}>Demand</Text> : null}
        {hasIndices ? <Text style={s.cellScore}>Opp.</Text> : null}
        <Text style={s.cellCov}>Coverage</Text>
        <Text style={s.cellRead}>Read</Text>
      </View>
      {report.per_product.map((p) => (
        <View key={p.audit_id} style={s.row} wrap={false}>
          <Text style={s.cellName}>{p.title}</Text>
          <Text style={[s.cellScore, { color: tone(p.overall_score) }]}>{p.overall_score == null ? '—' : String(p.overall_score)}</Text>
          {hasIndices ? <Text style={[s.cellScore, { color: tone(p.demand_index) }]}>{p.demand_index == null ? '—' : String(p.demand_index)}</Text> : null}
          {hasIndices ? <Text style={[s.cellScore, { color: tone(p.opportunity_index) }]}>{p.opportunity_index == null ? '—' : String(p.opportunity_index)}</Text> : null}
          <Text style={s.cellCov}>{p.coverage}</Text>
          <Text style={s.cellRead}>{p.one_line || p.summary || '—'}</Text>
        </View>
      ))}

      {report.top_opportunities && report.top_opportunities.length ? <Opportunities rows={report.top_opportunities} title="Top content opportunities" showProducts /> : null}

      <Text style={s.h2}>Cross-product themes</Text>
      {report.cross_product_themes.length ? report.cross_product_themes.map((t, i) => (
        <View key={i} style={s.item} wrap={false}>
          <Text style={s.itemTitle}>{t.theme} <Text style={{ color: MUTED }}>({t.sentiment}; {t.products.join(', ')})</Text></Text>
          <Text style={s.itemSub}>{t.insight}</Text>
        </View>
      )) : <Text style={s.itemSub}>No theme recurred across products.</Text>}

      {([
        ['Strongest praise', report.strongest_praise],
        ['Recurring pain points', report.recurring_pain_points],
        ['Recurring questions', report.recurring_questions],
      ] as Array<[string, string[]]>).map(([title, items]) => (
        <View key={title}>
          <Text style={s.h2}>{title}</Text>
          {items.length ? items.map((x, i) => <Text key={i} style={s.item}>• {x}</Text>) : <Text style={s.itemSub}>None.</Text>}
        </View>
      ))}
    </>
  );
}

const componentsLine = (block?: { components: Record<string, number | null> } | null) =>
  block && block.components
    ? Object.keys(block.components)
        .filter((k) => block.components[k] != null)
        .map((k) => `${k.replace(/_/g, ' ')} ${block.components[k]}`)
        .join(' · ')
    : '';

export function ConsumerVoicePdf({ audit }: { audit: ConsumerVoiceAudit }) {
  const { report, subscores: sub } = audit;
  const subject = audit.kind === 'brand' ? audit.params.brand_name : audit.params.product_name || audit.url;
  const meta = report && report.kind === 'product' ? (report as ConsumerVoiceProductReport).meta : undefined;
  const renderBody = () => {
    if (!report) {
      return <Text style={s.body}>{audit.status === 'error' ? `This analysis failed: ${audit.generation_error || 'unknown error'}.` : 'Report not available.'}</Text>;
    }
    if (report.kind === 'brand') return <BrandPages report={report as ConsumerVoiceBrandReport} />;
    return <ProductPages audit={audit} report={report as ConsumerVoiceProductReport} />;
  };
  const cards: Array<{ label: string; value: number | null; caption: string }> = [
    {
      label: 'Consumer Voice',
      value: audit.overall_score,
      caption: sub
        ? `Sentiment ${sub.sentiment ?? '—'} · Advocacy ${sub.advocacy ?? '—'} · Objection severity ${sub.objection_severity ?? '—'}`
        : 'How consumers feel',
    },
    {
      label: 'Consumer Demand',
      value: audit.demand_index,
      caption: audit.demand_index == null ? 'No search-demand signal in this run' : componentsLine(meta && meta.demand) || 'Search, ask and discuss',
    },
    {
      label: 'Content Opportunity',
      value: audit.opportunity_index,
      caption: audit.opportunity_index == null ? 'Needs a demand signal' : componentsLine(meta && meta.opportunity) || 'Demand × unanswered needs × page gaps',
    },
  ];
  const objectiveCards = CV_OBJECTIVE_SCORES.map((o) => ({
    label: `${o.label} · ${o.objective}`,
    value: audit[o.key] == null ? null : (audit[o.key] as number),
    caption: (meta && meta.objective_scores && componentsLine(meta.objective_scores[o.key.replace('_score', '') as 'trust' | 'education' | 'hype'])) || o.caption,
  }));
  const anyObjective = objectiveCards.some((c) => c.value != null);
  return (
    <Document title={`Consumer Voice — ${subject || audit.id}`}>
      <Page size="A4" style={s.page}>
        <Text style={s.kicker}>The Desire Company · Consumer Voice {audit.kind === 'brand' ? 'Brand Analysis' : 'Analysis'}</Text>
        <Text style={s.title}>{subject}</Text>
        <Text style={s.meta}>
          {audit.kind === 'product' && audit.params.brand_name ? `${audit.params.brand_name} · ` : ''}
          {audit.params.category ? `${audit.params.category} · ` : ''}
          {audit.scored_at ? new Date(audit.scored_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
        </Text>
        <View style={s.strip}>
          {cards.map((c, i) => (
            <View key={c.label} style={i === cards.length - 1 ? [s.card, s.cardLast] : s.card}>
              <Text style={s.cardLabel}>{c.label}</Text>
              <Text style={[s.cardValue, { color: tone(c.value) }]}>{c.value == null ? '—' : String(c.value)}</Text>
              <Text style={s.cardCaption}>{c.caption}</Text>
            </View>
          ))}
        </View>
        {anyObjective ? (
          <View style={s.stripSmall}>
            {objectiveCards.map((c, i) => (
              <View key={c.label} style={i === objectiveCards.length - 1 ? [s.card, s.cardLast] : s.card}>
                <Text style={s.cardLabel}>{c.label}</Text>
                <Text style={[s.cardValueSmall, { color: tone(c.value) }]}>{c.value == null ? '—' : String(c.value)}</Text>
                <Text style={s.cardCaption}>{c.caption}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {renderBody()}

        <View style={s.footer} fixed>
          <Text>Consumer Voice · The Desire Company · Informational; may contain inaccuracies.</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
